import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShoppingBag } from 'lucide-react';

export const Login = () => {
  const [loading, setLoading] = useState(null); // 'google' | 'guest' | null
  const { loginWithGoogle, loginAsGuest } = useAuth();
  const navigate = useNavigate();

  const friendlyAuthError = (error) => {
    const code = error?.code || '';
    const map = {
      'auth/popup-closed-by-user': 'Sign-in window was closed. Please try again.',
      'auth/popup-blocked': 'Popup blocked — please allow popups for this site.',
      'auth/network-request-failed': 'Network error. Check your internet connection.',
      'auth/too-many-requests': 'Too many attempts. Please wait a minute and try again.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Contact support.',
      'auth/unauthorized-domain': 'This domain is not authorized for sign-in. Contact support.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/cancelled-popup-request': 'Another sign-in is in progress.',
    };
    return map[code] || error?.message?.replace(/^Firebase:\s*/, '').replace(/\s*\(auth\/[a-z0-9-]+\)\.?/i, '') || 'Something went wrong. Please try again.';
  };

  const handleGoogleLogin = async () => {
    setLoading('google');
    try {
      await loginWithGoogle();
      toast.success('Logged in with Google!');
      navigate('/');
    } catch (error) {
      console.error('Google auth error:', error?.code, error?.message);
      toast.error(friendlyAuthError(error));
    } finally {
      setLoading(null);
    }
  };

  const handleGuest = async () => {
    setLoading('guest');
    try {
      await loginAsGuest();
      toast.success('Browsing as guest');
      navigate('/');
    } catch (error) {
      console.error('Guest error:', error?.message);
      toast.error('Could not start guest session');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-6 bg-gradient-to-br from-white via-[#FDFBF5] to-white">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#9B7F22] shadow-lg mb-4">
            <ShoppingBag size={36} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-[#0A1F44] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="auth-title">
            Welcome to Zindal
          </h1>
          <p className="text-gray-600 mt-2">Premium lighting for every moment</p>
        </div>

        {/* Card */}
        <div className="card p-8 bg-white border border-gray-200 shadow-md">
          <p className="text-sm text-gray-700 text-center mb-5">
            Sign in to <b>track orders</b>, view <b>invoices</b>, and save <b>shipping addresses</b>.
          </p>

          {/* Google */}
          <Button
            onClick={handleGoogleLogin}
            type="button"
            disabled={loading !== null}
            className="w-full h-12 bg-white border-2 border-gray-300 text-gray-800 hover:bg-gray-50 hover:border-[#D4AF37] transition font-semibold text-sm"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading === 'google' ? 'Connecting…' : 'Continue with Google'}
          </Button>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white text-gray-500 font-medium">OR</span>
            </div>
          </div>

          {/* Guest */}
          <Button
            onClick={handleGuest}
            type="button"
            disabled={loading !== null}
            className="w-full h-12 bg-[#0A1F44] text-white hover:bg-[#16326B] transition font-semibold text-sm"
            data-testid="guest-login-btn"
          >
            {loading === 'guest' ? 'Starting…' : 'Continue as Guest'}
          </Button>

          <p className="text-center text-[11px] text-gray-500 mt-4 leading-relaxed">
            As a guest you can shop and place orders. Sign in with Google later to save and track them.
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms</Link> and{' '}
          <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};
